import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { FootprintsSelectors } from '@nibtravel/shared/data-access/footprints';
import { AppConfigState } from '@nibtravel/shared/util/app-config';
import {
  EcommerceProduct,
  EcommerceService,
  ProductCategory,
} from '@nibtravel/shared/util/gtm';
import { IBasicOptionInput, OptionType } from '@nibtravel/shared/util/models';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { PurchasePathDimensions } from '../custom-dimension/purchase-path-dimension.service';
import { PurchasePath } from './purchase-path.selectors';

@Injectable()
export class OptionsAnalyticService {
  readonly previousOption: Record<string, EcommerceProduct | undefined> = {};
  brand = this.store.selectSnapshot(AppConfigState.getBrand);

  constructor(
    private store: Store,
    private currencyPipe: CurrencyPipe,
    private dimensionService: PurchasePathDimensions,
    private ecommerceService: EcommerceService
  ) {}

  getProducts(): Observable<EcommerceProduct[]> {
    return combineLatest([
      this.getBasicOptionProducts(),
      this.getFootprints(),
      this.dimensionService.getDimension(),
    ]).pipe(
      map(([basicProduct, footprints, dimensions]) =>
        basicProduct.concat(footprints).map((ep) => ({ ...ep, ...dimensions }))
      )
    );
  }

  updateCancellationOption(): void {
    const tripCancellation = this.store.selectSnapshot(
      PurchasePath.tripCancellation
    );
    if (!tripCancellation?.cancellationCoverLimit) {
      return;
    }

    const name = this.getOptionName(tripCancellation.optionType);
    const inputProduct = {
      id: name,
      name: name,
      brand: this.brand,
      category: ProductCategory.Options,
      quantity: 1,
      price: tripCancellation.cancellationCoverLimit,
    };

    return inputProduct
      ? this.updateOption(inputProduct, tripCancellation.optionType)
      : undefined;
  }

  updateBasicOption(input: IBasicOptionInput): void {
    const inputProduct = this.getOptionProduct(input);
    return inputProduct
      ? this.updateOption(inputProduct, input.optionType)
      : undefined;
  }

  private updateOption(inputProduct: EcommerceProduct, optionType: OptionType) {
    const previousInput = this.previousOption[optionType];
    if (previousInput) {
      this.ecommerceService.removeFromCart([previousInput]);
    }
    this.dimensionService
      .getDimension()
      .pipe(take(1))
      .subscribe((dimension) => {
        const mergedOption = { ...inputProduct, ...dimension };
        this.previousOption[optionType] = mergedOption;
        this.ecommerceService.addToCart([mergedOption]);
      });
  }

  private getFootprints(): Observable<EcommerceProduct[]> {
    return this.store.select(FootprintsSelectors.footprintsAnalytics).pipe(
      map((footprints) => {
        if (!footprints) {
          return [];
        }

        const brand = this.store.selectSnapshot(AppConfigState.getBrand);
        return [
          {
            id: footprints.project.charity.name,
            name: 'Footprints donation',
            variant: undefined,
            brand: brand,
            category: ProductCategory.Options,
            quantity: 1,
            price: footprints.donation.donationAmount,
          },
        ] as EcommerceProduct[];
      })
    );
  }

  private getBasicOptionProducts(): Observable<EcommerceProduct[]> {
    return this.store.select(PurchasePath.basicOptions).pipe(
      map((basicOptions: IBasicOptionInput[] | undefined) => {
        return basicOptions
          ? (basicOptions
              .filter((bo) => !!bo)
              .map((bo) => this.getOptionProduct(bo)) as EcommerceProduct[])
          : [];
      })
    );
  }

  private getOptionProduct(
    input: IBasicOptionInput
  ): EcommerceProduct | undefined {
    const optionPrices = this.store.selectSnapshot(PurchasePath.optionPrices);
    const price = optionPrices
      ? optionPrices.find((bo) => bo.optionType === input.optionType)?.price
      : undefined;
    if (!price) {
      return undefined;
    }
    const name = this.getOptionName(input.optionType);
    const variant = this.getBasicOptionVariant(input);
    const brand = this.store.selectSnapshot(AppConfigState.getBrand);

    return {
      name: name,
      variant: variant,
      brand: brand,
      category: ProductCategory.Options,
      quantity: 1,
      price: price,
    };
  }
  private getOptionName(optionType: OptionType) {
    switch (optionType) {
      case OptionType.PolicyExcessBuyoutPercentage:
        return 'Policy excess';
      case OptionType.SnowSports:
        return 'Snow sports';
      case OptionType.RentalVehicleExcess:
        return 'Rental car';
      case OptionType.TripCancellationFixed:
      case OptionType.TripCancellationPercentage:
      case OptionType.PreEx:
        return 'Trip Cancellation';
      default:
        return `Unhandled option type: ${optionType}`;
    }
  }
  private getBasicOptionVariant(input: IBasicOptionInput) {
    switch (input.optionType) {
      case OptionType.PolicyExcessBuyoutPercentage:
        return `${
          input.isOptedIn ? this.getCurrencyValue(input.optedInTotalValue) : 0
        }`;
      case OptionType.SnowSports:
        return input.isOptedIn ? 'Yes' : 'No';
      case OptionType.RentalVehicleExcess:
        return input.isOptedIn
          ? `${this.getCurrencyValue(input.optedInTotalValue)}`
          : 'No';
      default:
        return '';
    }
  }

  private getCurrencyValue(value: string | number | undefined): string | null {
    const currencyCode = this.store.selectSnapshot(PurchasePath.currencyCode);
    return this.currencyPipe.transform(
      value || 0,
      currencyCode,
      undefined,
      '0.0-0'
    );
  }
}
